import { Tokens } from '@/assets/tokens'
import { Button, Divider, Flex, IconTooltip, SelectedList, Switch } from '@/components'
import { Item, useSelectedList } from '@/components/core/SelectedList/SelectedList.hooks'
import { ManualCard } from '@/components/derived'
import { FlexTable, FlexTableColumnType } from '@/components/derived/FlexTable/FlexTable.component'
import { FullScreenModal } from '@/components/derived/FullScreenModal/FullScreenModal.component'
import { useRouting } from '@/hooks'
import { Alert, Api, Confirmation } from '@/modules'
import { Str } from '@/utils/strings'
import { getFromToElements } from '@/utils/pagination'
import { DeleteFive, Edit, Key, Plus } from '@icon-park/react'
import { KeyMessageResponse, Paged, SearchResponse, ToggleKeyMessageActiveRequest } from '@shared/models'
import { RefreshCw } from 'lucide-react'
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useCompanyContext } from '../CompanyDetails.context'
import { getDescription } from '../utils'
import {
  FavorabilityDescription,
  FavorabilityToggleContainer,
  FormSection,
  FormTitle,
  KeyMessageContainer,
  KeyMessageFiltersContainer,
  LoadingContainer,
  MessageCell,
  PremiseTag,
  PremisesPreview,
  TableSection,
} from './KeyMessage.styles'
import { CreateKeyMessageModal } from './components/CreateKeyMessageModal.component'
import { EditKeyMessageModal } from './components/EditKeyMessageModal.component'

const KEY_MESSAGE_PAGE_SIZE = 10

export const KeyMessage = memo(Component)

function Component(): JSX.Element | null {
  const { companyID, changeQueryParam, queryParams, currentPage } = useRouting()
  const { company, reloadCompany } = useCompanyContext()
  const { sendAlert } = Alert.Hook()

  const [considerFavorability, setConsiderFavorability] = useState(false)

  const { binder: brandFilterBinder, selectedOptions: selectedBrandsForFilter } = useSelectedList(() => {})
  const activeKeyMessagesBinder = Switch.Hook(true)

  const [keyMessages, setKeyMessages] = useState<KeyMessageResponse[]>([])
  const [isLoadingKeyMessages, setIsLoadingKeyMessages] = useState(false)
  const [selectedKeyMessage, setSelectedKeyMessage] = useState<KeyMessageResponse | null>(null)
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false)

  const { isRunning: isToggling, run: toggleKeyMessage } = Api.Command<void>(
    Api.Methods.PATCH,
    'key-messages/:companyID/:internalID/toggle',
  )
  const { run: updateCompany } = Api.Command<void>(Api.Methods.PATCH, 'companies/:companyID')
  const [loadKeyMessagesList, isLoadingList, keyMessagesResponse] =
    Api.LazyQuery<Paged<KeyMessageResponse>>('key-messages')

  const [isSearchingBrands, , searchedBrands] = Api.Query<SearchResponse[]>(
    `brands/search?company=${companyID}&size=10000`,
  )

  const { open: openDeactivateConfirmation } = Confirmation.Hook({
    icon: DeleteFive,
    title: 'Você tem certeza?',
    text: 'Deseja desativar a mensagem-chave?',
    confirmText: 'Sim! Desativar mensagem-chave.',
  })

  const selectedBrandsForFilterParsed = selectedBrandsForFilter.map((item: Item) => item.value).join(',')

  const loadKeyMessages = useCallback(() => {
    if (!companyID) return

    setIsLoadingKeyMessages(true)
    loadKeyMessagesList({
      company: companyID,
      page: currentPage,
      size: KEY_MESSAGE_PAGE_SIZE,
      brandIDs: selectedBrandsForFilterParsed || undefined,
      isActive: activeKeyMessagesBinder.isChecked,
    })
  }, [companyID, currentPage, selectedBrandsForFilterParsed, activeKeyMessagesBinder.isChecked])

  useEffect(() => {
    if (keyMessagesResponse) {
      setKeyMessages(keyMessagesResponse.elements || [])
      setIsLoadingKeyMessages(false)
    }
  }, [keyMessagesResponse])

  const prevKeyMessageFiltersRef = useRef({ brands: '', isActive: true })

  useEffect(() => {
    if (queryParams.modal !== 'mensagem-chave') return

    const filtersChanged =
      prevKeyMessageFiltersRef.current.brands !== selectedBrandsForFilterParsed ||
      prevKeyMessageFiltersRef.current.isActive !== activeKeyMessagesBinder.isChecked

    prevKeyMessageFiltersRef.current = {
      brands: selectedBrandsForFilterParsed,
      isActive: activeKeyMessagesBinder.isChecked,
    }

    if (filtersChanged && currentPage !== '1') {
      changeQueryParam({ param: 'pagina', value: '1' })
      return
    }

    loadKeyMessages()
  }, [queryParams.modal, loadKeyMessages, selectedBrandsForFilterParsed, activeKeyMessagesBinder.isChecked])

  useEffect(() => {
    if (company) {
      setConsiderFavorability(company.considerFavorability || false)
    }
  }, [company])

  function handleEdit(keyMessage: KeyMessageResponse): void {
    if (keyMessage.message === 'SEM REGRA') {
      return sendAlert({
        type: 'error',
        title: 'Ação não permitida',
        message: 'A mensagem-chave "SEM REGRA" não pode ser editada',
      })
    }

    setSelectedKeyMessage(keyMessage)
    changeQueryParam({
      param: 'modal',
      value: 'edit-key-message',
      replaceAll: true,
    })
  }

  function handleToggleActive(keyMessage: KeyMessageResponse, newActiveState: boolean): void {
    if (keyMessage.message === 'SEM REGRA') {
      return sendAlert({
        type: 'error',
        title: 'Ação não permitida',
        message: 'A mensagem-chave "SEM REGRA" não pode ser desativada',
      })
    }

    const input: ToggleKeyMessageActiveRequest = {
      isActive: newActiveState,
    }

    toggleKeyMessage<ToggleKeyMessageActiveRequest>({
      binds: [
        ['companyID', String(companyID)],
        ['internalID', keyMessage.internalID],
      ],
      input,
      onSuccess: () => {
        sendAlert({
          type: 'success',
          title: 'Sucesso',
          message: `Mensagem-chave ${newActiveState ? 'ativada' : 'desativada'} com sucesso!`,
        })

        const willRemoveFromList = activeKeyMessagesBinder.isChecked && !newActiveState
        const pageWasAdjusted =
          willRemoveFromList && keyMessagesResponse?.total ?
            adjustKeyMessagePageIfNeeded(keyMessagesResponse.total - 1)
          : false

        if (!pageWasAdjusted) {
          loadKeyMessages()
        }
      },
      onFail: () => {
        sendAlert({
          type: 'error',
          title: 'Erro',
          message: 'Erro ao alterar status da mensagem-chave',
        })
      },
    })
  }

  function adjustKeyMessagePageIfNeeded(totalAfterAction: number): boolean {
    const currentPageNum = Number(currentPage)
    const totalPages = Math.ceil(totalAfterAction / KEY_MESSAGE_PAGE_SIZE)

    if (currentPageNum > totalPages && totalPages > 0) {
      changeQueryParam({ param: 'pagina', value: String(totalPages) })
      return true
    }
    return false
  }

  const keyMessageRows = useMemo<KeyMessageResponse[]>(() => {
    return keyMessages.flatMap((item) => {
      const dataSources = Array.isArray(item.dataSource) ? item.dataSource : [item.dataSource]
      const brands = [item.internalID.split('_')[1]]

      return brands.flatMap((brand) =>
        dataSources.map((dataSource) => ({
          ...item,
          dataSource,
          dataSourceIDs: [dataSource.id],
          brands: [brand],
        })),
      )
    })
  }, [keyMessages])

  const manualCardKeyMessageCount = company?.counts?.keyMessageCounts ?? 0
  const keyMessageScreenCount = keyMessagesResponse?.total ?? keyMessages.length

  function handleFavorabilityToggle(): void {
    if (!companyID) return

    const newActiveState = !considerFavorability
    setConsiderFavorability(newActiveState)

    updateCompany<{ considerFavorability: boolean }>({
      binds: [['companyID', companyID]],
      input: { considerFavorability: newActiveState },
      onSuccess: () => {
        sendAlert({
          type: 'success',
          title: 'Sucesso',
          message: 'Configuração de favorabilidade atualizada com sucesso!',
        })
        reloadCompany()
      },
      onFail: () => {
        sendAlert({
          type: 'error',
          title: 'Erro',
          message: 'Erro ao atualizar configuração de favorabilidade',
        })
        setConsiderFavorability(!newActiveState)
      },
    })
  }

  function renderPremisesPreview(premises: string): JSX.Element {
    const premisesList = premises
      .split(';')
      .map((p) => p.trim())
      .filter((p) => p.length > 0)
    const maxVisible = 3
    const visiblePremises = premisesList.slice(0, maxVisible)
    const remainingCount = premisesList.length - maxVisible

    return (
      <PremisesPreview>
        {visiblePremises.map((premise, index) => (
          <PremiseTag key={index} title={premise}>
            {premise}
          </PremiseTag>
        ))}
        {remainingCount > 0 && (
          <PremiseTag title={premisesList.slice(maxVisible).join('; ')}>+{remainingCount} mais</PremiseTag>
        )}
      </PremisesPreview>
    )
  }

  const columns: FlexTableColumnType<KeyMessageResponse>[] = [
    {
      key: 'brands',
      title: 'Marca',
      width: '180px',
      align: 'center',
      render: (item) => <span>{item.brandName}</span>,
    },
    {
      key: 'message',
      title: 'Mensagem',
      width: '320px',
      minWidth: 260,
      render: (item) => <MessageCell title={item.message}>{item.message}</MessageCell>,
    },
    {
      key: 'premises',
      title: 'Premissas',
      render: (item) => renderPremisesPreview(item.premises),
    },
    {
      key: 'Fonte',
      title: 'Fonte',
      width: '140px',
      align: 'center',
      render: (item) => {
        const dataSources = Array.isArray(item.dataSource) ? item.dataSource : [item.dataSource]
        const names = dataSources.map((dataSource) => (dataSource.id === 1 ? 'Redes' : 'Imprensa'))
        return <span>{names.join(', ')}</span>
      },
    },
    {
      key: 'status',
      title: (
        <Flex $type="row" $align="center" $justify="center" $gap={8}>
          <span>Ativo</span>
          <Button.Root onClick={loadKeyMessages} $variant="link">
            <IconTooltip
              tooltipText="Atualizar listagem"
              icon={<RefreshCw size={16} color={Tokens.ColorTextSecondary} />}
            />
          </Button.Root>
        </Flex>
      ),
      width: '100px',
      align: 'center',
      render: (item) => (
        <Switch.Root
          isChecked={item.isActive}
          disabled={item.message === 'SEM REGRA'}
          toggle={() => {
            const newActiveState = !item.isActive
            if (item.isActive) {
              openDeactivateConfirmation(() => handleToggleActive(item, newActiveState))
            } else {
              handleToggleActive(item, newActiveState)
            }
          }}
        />
      ),
    },
    {
      key: 'actions',
      title: 'Opções',
      width: '80px',
      align: 'center',
      render: (item) => (
        <Button.Root
          type="button"
          $variant="link"
          style={{ color: Tokens.ColorUIPrimary }}
          onClick={() => handleEdit(item)}
          disabled={isToggling || item.message === 'SEM REGRA'}
          title={item.message === 'SEM REGRA' ? 'Mensagem-chave não editável' : 'Editar premissas'}>
          <Button.Icon value={Edit} />
        </Button.Root>
      ),
    },
  ]

  if (!companyID || !company) return null

  return (
    <>
      <ManualCard
        icon={Key}
        healthCheck={company.healthCheck?.isKeyMessagesChecked}
        title="Mensagem-Chave"
        tooltip="Cadastre as mensagens estratégicas que sua marca deseja comunicar. O sistema irá identificar e analisar a presença desses discursos nas menções coletadas na mídia."
        description={getDescription(manualCardKeyMessageCount)}
        linkName="Editar"
        linkAction={() =>
          changeQueryParam({
            param: 'modal',
            value: 'mensagem-chave',
            replaceAll: true,
          })
        }
      />

      <FullScreenModal
        controlKey="mensagem-chave"
        backToName={company.name}
        title="Mensagem-Chave"
        subtitle="Configure as mensagens-chave que serão utilizadas na classificação de menções"
        icon={Key}
        beforeClose={() => {
          reloadCompany()
        }}>
        <KeyMessageContainer>
          <FormSection>
            <FormTitle>Configurações Gerais</FormTitle>
            <FavorabilityToggleContainer>
              <Switch.Root isChecked={considerFavorability} toggle={handleFavorabilityToggle} />
              <FavorabilityDescription>
                Considerar regras de favorabilidade (positivo/favorável) para esta empresa durante a classificação de
                mensagem chave
              </FavorabilityDescription>
            </FavorabilityToggleContainer>
          </FormSection>

          <TableSection>
            <Divider.Root>
              <Divider.Title value="Mensagens-Chave Cadastradas" />
              <Divider.Spacer />
              <Divider.Subtitle
                value={`${getFromToElements(currentPage, keyMessages.length)}/${keyMessageScreenCount} mensagens`}
              />
              <Button.Root type="button" $variant="primary" onClick={() => setIsCreateModalOpen(true)}>
                <Button.Icon value={Plus} />
                <Button.Text value="Nova Mensagem-Chave" />
              </Button.Root>
            </Divider.Root>

            <KeyMessageFiltersContainer>
              <SelectedList
                placeholder="Marcas"
                isLoading={isSearchingBrands}
                options={
                  searchedBrands?.map((item): Item => ({ text: Str.from(item.text).initCap, value: item.value })) ?? []
                }
                {...brandFilterBinder}
              />
              <Switch.Root label="Mostrar apenas mensagens-chave ativas" {...activeKeyMessagesBinder} direction="row" />
            </KeyMessageFiltersContainer>

            {isLoadingKeyMessages || isLoadingList ?
              <LoadingContainer>Carregando mensagens-chave...</LoadingContainer>
            : <FlexTable
                columns={columns}
                data={keyMessageRows}
                totalPages={keyMessagesResponse?.pages}
                isLoading={isLoadingKeyMessages || isLoadingList}
                enableSorting={false}
              />
            }
          </TableSection>
        </KeyMessageContainer>
      </FullScreenModal>

      <CreateKeyMessageModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSuccess={loadKeyMessages}
      />

      <EditKeyMessageModal
        keyMessage={selectedKeyMessage}
        onSuccess={() => {
          loadKeyMessages()
          setSelectedKeyMessage(null)
        }}
      />
    </>
  )
}
